/** Stage + status colour tokens. Mirrors the PO status-style approach. */

/**
 * Record types: every product_dev row is a new product (the default), a
 * change to an existing product, or a general task. Tasks have no product
 * type and run the simple task stage chain.
 */
export const RECORD_TYPES = ['new_product', 'product_change', 'task'] as const;
export type ProductDevRecordType = (typeof RECORD_TYPES)[number];

export const RECORD_TYPE_LABEL: Record<string, string> = {
  new_product: 'New product',
  product_change: 'Product change',
  task: 'Task',
};

export const PRODUCT_DEV_STAGES = [
  'brief',
  'initial_sample',
  'pre_production_sample',
  'bulk_complete',
  'revision',
] as const;
export type ProductDevStage = (typeof PRODUCT_DEV_STAGES)[number];

/** The 4-step pipeline strip only shows the linear flow. `revision` is a
 *  branch stage applied AFTER a product is already approved, so it sits
 *  outside the progress strip and renders as a standalone pill. */
export const PRODUCT_DEV_PIPELINE_STAGES = PRODUCT_DEV_STAGES.filter(
  (s) => s !== 'revision',
);

/** Task stage chain (record_type 'task'). 'done' is the task-terminal
 *  stage — it plays the role bulk_complete plays on the garment chain. */
export const PRODUCT_DEV_TASK_STAGES = ['open', 'in_progress', 'done'] as const;
export type ProductDevTaskStage = (typeof PRODUCT_DEV_TASK_STAGES)[number];

/** The pipeline-strip chain for a given record type. */
export const pipelineStagesForRecordType = (
  recordType: string | null | undefined,
): readonly string[] =>
  recordType === 'task' ? PRODUCT_DEV_TASK_STAGES : PRODUCT_DEV_PIPELINE_STAGES;

export const PRODUCT_DEV_STATUSES = [
  'briefing',
  'awaiting_sample',
  'in_review',
  'revision_requested',
  'approved',
  'rejected',
  'on_hold',
] as const;
export type ProductDevStatus = (typeof PRODUCT_DEV_STATUSES)[number];

export const STAGE_LABEL: Record<string, string> = {
  brief: 'Brief',
  initial_sample: 'Initial Sample',
  pre_production_sample: 'Pre-Production Sample',
  bulk_complete: 'Bulk / Complete',
  revision: 'Revision',
  // Task chain
  open: 'Open',
  in_progress: 'In Progress',
  done: 'Done',
};

export const STAGE_PILL_CLASS: Record<string, string> = {
  brief: 'bg-slate-100 text-slate-700',
  initial_sample: 'bg-blue-100 text-blue-800',
  pre_production_sample: 'bg-violet-100 text-violet-800',
  bulk_complete: 'bg-emerald-100 text-emerald-800',
  revision: 'bg-orange-100 text-orange-800',
  // Task chain — mirrors the garment chain's start/middle/terminal palette.
  open: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-blue-100 text-blue-800',
  done: 'bg-emerald-100 text-emerald-800',
};

export const STATUS_LABEL: Record<string, string> = {
  briefing: 'Briefing',
  awaiting_sample: 'Awaiting Sample',
  in_review: 'In Review',
  revision_requested: 'Revision Requested',
  approved: 'Approved',
  rejected: 'Rejected',
  on_hold: 'On Hold',
};

export const STATUS_PILL_CLASS: Record<string, string> = {
  // Matches the 'brief' stage pill — both mean "not started yet".
  briefing: 'bg-slate-100 text-slate-700',
  awaiting_sample: 'bg-amber-100 text-amber-800',
  in_review: 'bg-blue-100 text-blue-800',
  revision_requested: 'bg-orange-100 text-orange-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-800',
  on_hold: 'bg-slate-100 text-slate-700',
};

/**
 * Group ordering — worklist philosophy, not lifecycle order.
 *
 * A dev record's status list is scanned to answer "what do I act on today",
 * so groups sort by how much they need someone's attention right now, NOT by
 * where they sit in the brief → sample → approve pipeline:
 *   1. `in_review`          — the sample is here; the TEAM owes a decision today.
 *   2. `revision_requested` — we kicked it back; active iteration we must keep
 *                             from stalling at the supplier.
 *   3. `awaiting_sample`    — the supplier is making the first sample; active
 *                             but fully supplier-blocked and earlier.
 *   4. `briefing`           — not started yet, still being written up.
 *   5. `on_hold`            — deliberately parked.
 *   6. `approved` / 7. `rejected` — terminal; history, sink to the bottom.
 * (Entry-first / "briefing at the top" was rejected: it buries the rows that
 * need a human decision under the ones that have not started.)
 *
 * Shared by the buyer list (`pages/ProductDev.tsx`) and the partner mirror
 * (`pages/partner/SharedProductDev.tsx`) — both grouped surfaces read the same
 * order from here, so a new status can only be added in one place.
 */
export const STATUS_GROUP_ORDER: readonly string[] = [
  'in_review',
  'revision_requested',
  'awaiting_sample',
  'briefing',
  'on_hold',
  'approved',
  'rejected',
];

/**
 * Stage ordering — same worklist philosophy as STATUS_GROUP_ORDER: the stage
 * nearest a decision leads, not-yet-started next, terminal last. Garment chain
 * first, then the task chain (both coexist on the "All" chip).
 *   pre_production_sample — final sample gate before bulk; needs sign-off.
 *   revision              — active rework after a problem was found.
 *   initial_sample        — first sample under way.
 *   brief                 — not started.
 *   bulk_complete         — terminal (garment).
 *   in_progress / open / done — task chain: active, not-started, terminal.
 */
export const STAGE_GROUP_ORDER: readonly string[] = [
  'pre_production_sample',
  'revision',
  'initial_sample',
  'brief',
  'bulk_complete',
  'in_progress',
  'open',
  'done',
];

/**
 * Triage priority. A record is `urgent`, `high`, or has no flag (null). There
 * is deliberately NO neutral/none member in the tuple — a null priority renders
 * NOTHING in the list (no grey noise), so the map only carries the two active
 * levels. Mirrors the STATUS/STAGE map convention above.
 */
export const PRODUCT_DEV_PRIORITIES = ['urgent', 'high'] as const;
export type ProductDevPriority = (typeof PRODUCT_DEV_PRIORITIES)[number];

/** i18n label keys resolve under `priority.*`; these English fallbacks mirror
 *  the other *_LABEL maps in this file for non-i18n call sites. */
export const PRIORITY_LABEL: Record<string, string> = {
  urgent: 'Urgent',
  high: 'High',
};

/** Flag icon fill + text colour per level. Filled rose-600 for urgent, filled
 *  amber-500 for high — both are reserved semantic colours (DESIGN.md §1:
 *  amber = attention, rose = the overdue/alarm tone already used on this list).
 *  Empty (null) priority is handled by the component, not this map. */
export const PRIORITY_ICON_CLASS: Record<string, string> = {
  urgent: 'fill-rose-600 text-rose-600',
  high: 'fill-amber-500 text-amber-500',
};

/** Sort rank for the secondary priority sort inside every grouping mode:
 *  urgent (0) leads, then high (1), then everything unflagged (2). JS sort is
 *  stable, so equal ranks preserve the incoming server order. */
export const priorityRank = (p: string | null | undefined): number =>
  p === 'urgent' ? 0 : p === 'high' ? 1 : 2;

/** Narrow a server value (free-form `text` column) to the two real flag
 *  levels. Anything else — null, a legacy value, a typo — reads as "no
 *  priority" and renders nothing, which is the deliberate empty state. Use
 *  this at every `PriorityFlag` call site instead of re-deriving it. */
export const asProductDevPriority = (v: unknown): ProductDevPriority | null =>
  v === 'urgent' || v === 'high' ? v : null;

/** Click-to-cycle order for the flag control: none → high → urgent → none. */
export const nextPriority = (
  p: ProductDevPriority | null,
): ProductDevPriority | null =>
  p === null ? 'high' : p === 'high' ? 'urgent' : null;

export const ATTRIBUTE_KIND_LABEL: Record<string, string> = {
  dye_method: 'Dye method',
  wash: 'Wash',
  embellishment: 'Embellishment',
  trim_type: 'Trim type',
};

export const DOC_TYPE_LABEL: Record<string, string> = {
  tech_pack: 'Tech pack',
  bom: 'BOM',
  spec_sheet: 'Spec sheet',
  lab_dip: 'Lab dip',
  strike_off: 'Strike-off',
  sample_photo: 'Sample photo',
  care_label: 'Care label',
  hangtag_artwork: 'Hangtag artwork',
  print_file: 'Print file',
  approval_ref: 'Approval reference',
  wash_standard: 'Wash standard',
  supplier_correspondence: 'Supplier correspondence',
  other: 'Other',
};

/**
 * Product types (phase-60 backend). The API rejects the retired
 * 'printed'/'cut-and-sew-*' values and the DB remaps them, so the label map
 * only carries the three canonical values. i18n keys resolve under
 * `productType.{blank,finished_garment,accessory}` and serve both this detail
 * page's dropdown and the list's ProductTypeBadge (single source of truth).
 */
export const PRODUCT_TYPES = [
  'blank',
  'finished_garment',
  'accessory',
] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];

export const PRODUCT_TYPE_LABEL: Record<string, string> = {
  blank: 'Blank',
  finished_garment: 'Finished garment',
  accessory: 'Accessory',
};

/** The "named slot" set surfaced in the Files card for cut-and-sew workflows.
 *  Ordering matters — slots render top-to-bottom in this sequence. BOM and
 *  Strike-off are intentionally omitted: BOM lines live alongside costing in
 *  practice, and strike-offs are rarely tracked as standalone documents at
 *  ThreadHeads scale. Both remain in DOC_TYPE_LABEL so historical uploads
 *  display correctly under "Other". */
export const NAMED_DOC_SLOTS: readonly string[] = [
  'tech_pack',
  'spec_sheet',
  'lab_dip',
  'sample_photo',
  'care_label',
  'hangtag_artwork',
];

export const INCOTERMS = ['FOB', 'EXW', 'DDP', 'CIF', 'FCA', 'DAP'] as const;
export type Incoterm = (typeof INCOTERMS)[number];

export const INCOTERM_LABEL: Record<string, string> = {
  FOB: 'FOB: Free On Board',
  EXW: 'EXW: Ex Works',
  DDP: 'DDP: Delivered Duty Paid',
  CIF: 'CIF: Cost, Insurance, Freight',
  FCA: 'FCA: Free Carrier',
  DAP: 'DAP: Delivered At Place',
};

export const SAMPLE_STATUSES = [
  'pending',
  'requested',
  'in_transit',
  'received',
  'approved',
  'rejected',
] as const;
export type SampleStatus = (typeof SAMPLE_STATUSES)[number];

export const SAMPLE_STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  requested: 'Requested',
  in_transit: 'In transit',
  received: 'Received',
  approved: 'Approved',
  rejected: 'Rejected',
};

export const SAMPLE_STATUS_PILL_CLASS: Record<string, string> = {
  pending: 'bg-muted text-muted-foreground ring-1 ring-border',
  requested: 'bg-slate-100 text-slate-700 ring-1 ring-slate-200',
  in_transit: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200',
  received: 'bg-blue-100 text-blue-800 ring-1 ring-blue-200',
  approved: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200',
  rejected: 'bg-rose-100 text-rose-800 ring-1 ring-rose-200',
};

/**
 * Per-colourway approval. The buyer click-cycles a variant through
 * VARIANT_STATUS_ORDER; the partner detail page renders the same pill
 * read-only. Semantic palette (DESIGN.md §1): muted = pending, emerald =
 * approved, rose = rejected, amber = resample. i18n labels resolve under
 * `variantStatus.*` — this map is colour only.
 */
export const VARIANT_STATUS_ORDER: readonly string[] = [
  'pending',
  'approved',
  'rejected',
  'resample',
];

export const VARIANT_STATUS_PILL_CLASS: Record<string, string> = {
  pending: 'bg-muted text-muted-foreground',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
  resample: 'bg-amber-100 text-amber-700',
};

/**
 * Sample-round QC checklist (BLU-629) — what went right and what went wrong on
 * a round. MUST stay in lockstep with `SAMPLE_QC_CHECKS` / `SAMPLE_QC_RESULTS`
 * in `apps/backend/src/modules/product-dev/product-dev.types.ts`: the backend is
 * the source of truth and the router rejects any kind or result not in its list.
 *
 * Order here is the display order — it follows the physical order a reviewer
 * works through a sample (measure it, look at it, feel it, check the trims, file
 * it), so `filing` stays last.
 */
export const SAMPLE_QC_CHECKS = [
  'measurement',
  'colour',
  'fabric',
  'labels_trims',
  'filing',
] as const;
export type SampleQcCheckKind = (typeof SAMPLE_QC_CHECKS)[number];

export const SAMPLE_QC_RESULTS = ['pass', 'fail', 'na'] as const;
export type SampleQcResult = (typeof SAMPLE_QC_RESULTS)[number];

/** Enum/key source only — display goes through the `sampleQc.*` i18n keys. */
export const SAMPLE_QC_CHECK_LABEL: Record<SampleQcCheckKind, string> = {
  measurement: 'Measurement',
  colour: 'Colour',
  fabric: 'Fabric',
  labels_trims: 'Labels & trims',
  filing: 'Label & file',
};

/** Semantic only, per DESIGN.md §1: emerald = passed, rose = failed, muted =
 *  not applicable. An UNSET check has no pill — it renders as a dashed outline,
 *  so "not reviewed yet" never reads as a verdict. */
export const SAMPLE_QC_RESULT_CLASS: Record<SampleQcResult, string> = {
  pass: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200',
  fail: 'bg-rose-100 text-rose-800 ring-1 ring-rose-200',
  na: 'bg-muted text-muted-foreground ring-1 ring-border',
};
